import { useSelector } from "react-redux";
import { SpinnerBlock } from "../common/Spinner";
import { withTranslation } from "react-i18next";
import PromotionCodeUpsertForm from "./PromotionCodeUpsertForm";
import { GoBackLinkButton } from "../../components/GoBackLinkButton";
import { routes } from "../../appRoutingHelpers";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreatePromotionCode,
  useGetPartnerships,
  useGetPromotionCode,
  useGetVouchers,
  useUpdatePromotionCode,
} from "../../service/api";
import { toastr } from "react-redux-toastr";
import { PromotionCodeFormType } from "./model/PromotionCodeSuccessResponse";
import * as authSelectors from "../auth/authSelectors";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";

const PromotionCodeUpsert = () => {
  const { id } = useParams<{
    id: string | undefined;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const isEdit = !!id;
  const navigate = useNavigate();
  const {
    data: partnerships,
    isLoading: isLoadingPartnerships,
    error: partnershipsError,
  } = useGetPartnerships(organizationKeyName);

  const {
    data: promotionCode,
    isLoading,
    error: promotionCodeError,
  } = useGetPromotionCode(organizationKeyName, isEdit, id);

  const {
    data: voucherTypes,
    isLoading: isLoadingVoucherTypeList,
    error: voucherTypesError,
  } = useGetVouchers(organizationKeyName);

  const {
    mutateAsync: createPromotionCode,
    isLoading: isCreating,
  } = useCreatePromotionCode();

  const {
    mutateAsync: updatePromotionCode,
    isLoading: isUpdating,
  } = useUpdatePromotionCode(id);

  const onSubmit = async (values: PromotionCodeFormType) => {
    try {
      if (isEdit) {
        await updatePromotionCode({
          data: values,
          id,
          organizationKeyName,
        });
      } else {
        await createPromotionCode({
          data: values,
          organizationKeyName,
        });
      }
      toastr.success(
        "Success",
        `Promotion code ${isEdit ? "updated" : "created"} successfully`
      );
      navigate(routes.promotionCodeList(organizationKeyName));
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  useHandleFetchError(voucherTypesError);
  useHandleFetchError(partnershipsError);
  useHandleFetchError(promotionCodeError);

  if (isLoading || isLoadingPartnerships || isLoadingVoucherTypeList) {
    return <SpinnerBlock />;
  }

  return (
    <div className="container mt-5">
      <GoBackLinkButton
        to={routes.promotionCodeList(organizationKeyName)}
        text="Back to all promotion codes"
      />
      <div className="row">
        <div className="col-12">
          <PromotionCodeUpsertForm
            onSubmit={(v: PromotionCodeFormType) => onSubmit(v)}
            isUpserting={isEdit ? isUpdating : isCreating}
            partnerships={partnerships || []}
            promotionCode={promotionCode}
            voucherTypes={voucherTypes}
          />
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(PromotionCodeUpsert);
